import { useMemo, useState } from "react";
import { Package, Search } from "lucide-react";
import { Dropdown } from "../../components/ui/dropdown";
import { ApiV2Error } from "../../lib/api-v2";
import { AddToCartButton } from "../cart/add-to-cart-button";
import { CartLink } from "../cart/cart-link";
import { ProductDescription } from "./product-description";
import { PlanAmountPicker } from "./plan-amount-picker";
import { ProductDetailsDrawer } from "./product-details-drawer";
import { ProductPrice } from "./product-price";
import { useProducts } from "./queries";
import { isPlan, type Product } from "./types";

const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiV2Error ? err.message : fallback);

type SortKey = "newest" | "price_asc" | "price_desc";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
] as const;

/** Points shown on a card before "+N more"; the details panel shows them all. */
const CARD_POINTS = 4;

/**
 * /products: the catalog TechLadder offers, read-only. Products are created by
 * TechLadder admins (/admin/products); an account browses them here.
 */
export function ProductsPage() {
  const products = useProducts();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("newest");
  const [selected, setSelected] = useState<Product | null>(null);

  const all = useMemo(() => products.data?.pages.flatMap((p) => p.items) ?? [], [products.data]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = q
      ? all.filter((p) => p.name.toLowerCase().includes(q) || (p.description ?? "").toLowerCase().includes(q))
      : all;
    // The API returns newest first; price sorts apply to what is loaded.
    if (sort === "price_asc") return [...matched].sort((a, b) => a.price - b.price);
    if (sort === "price_desc") return [...matched].sort((a, b) => b.price - a.price);
    return matched;
  }, [all, search, sort]);

  const loaded = !products.isLoading && !products.isError;
  const count = `${search ? `${visible.length} of ` : ""}${all.length}${products.hasNextPage ? "+" : ""} product${all.length === 1 ? "" : "s"}`;

  return (
    <div className="mx-auto max-w-7xl space-y-8 p-8">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-ink">Products</h1>
          <p className="mt-1.5 text-sm text-ink-2">Plans and credit packs available for your account.</p>
          <p className="mt-1 text-xs text-ink-3">
            {loaded && all.length > 0 && <span className="font-medium text-ink-2">{count} · </span>}
            All prices are exclusive of GST.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products"
              aria-label="Search products"
              className="h-10 w-full rounded-lg border border-line bg-elevated pl-9 pr-3 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
          <Dropdown<SortKey>
            value={sort}
            options={SORT_OPTIONS}
            onChange={setSort}
            ariaLabel="Sort products"
            align="right"
            className="h-10 bg-elevated sm:w-52"
          />
          <CartLink />
        </div>
      </header>

      {products.isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-4 rounded-2xl border border-line bg-elevated p-6">
              <div className="h-11 w-11 animate-pulse rounded-xl bg-soft" />
              <div className="h-5 w-2/3 animate-pulse rounded bg-soft" />
              <div className="space-y-2">
                <div className="h-3 animate-pulse rounded bg-soft" />
                <div className="h-3 w-4/5 animate-pulse rounded bg-soft" />
                <div className="h-3 w-3/5 animate-pulse rounded bg-soft" />
              </div>
              <div className="h-8 w-1/3 animate-pulse rounded bg-soft" />
              <div className="h-10 animate-pulse rounded-lg bg-soft" />
            </div>
          ))}
        </div>
      ) : products.isError ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-line bg-elevated p-12 text-center">
          <p className="text-sm text-danger">{errorMessage(products.error, "Could not load products.")}</p>
          <button onClick={() => { void products.refetch(); }} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
            Retry
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line bg-elevated px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-soft text-brand">
            <Package className="h-6 w-6" />
          </span>
          <p className="text-base font-semibold text-ink">
            {search ? "No products match your search" : "No products available yet"}
          </p>
          <p className="max-w-sm text-sm text-ink-2">
            {search ? "Try a different name or keyword." : "New plans and credit packs will appear here as soon as they're added."}
          </p>
        </div>
      ) : (
        <ul className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((product) => (
            <li
              key={product.id}
              aria-label={`Product ${product.name}`}
              className="group flex flex-col rounded-2xl border border-line bg-elevated p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand">
                  <Package className="h-5 w-5" />
                </span>
                <h2 className="line-clamp-2 pt-2 text-lg font-semibold leading-snug text-ink">{product.name}</h2>
              </div>

              <ProductDescription description={product.description} limit={CARD_POINTS} className="mt-5" />

              <div className="mt-auto pt-6">
                <div className="border-t border-line pt-5">
                  <ProductPrice product={product} />
                  {isPlan(product) ? (
                    <div className="mt-4 space-y-3">
                      <PlanAmountPicker product={product} />
                      <button
                        onClick={() => setSelected(product)}
                        className="w-full text-center text-sm font-semibold text-ink-2 hover:text-brand"
                      >
                        View details
                      </button>
                    </div>
                  ) : (
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setSelected(product)}
                        className="flex h-10 items-center justify-center rounded-lg border border-ink/15 bg-elevated px-4 text-sm font-semibold text-ink shadow-sm transition-colors hover:border-brand hover:text-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                      >
                        View details
                      </button>
                      <AddToCartButton productId={product.id} productName={product.name} />
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {products.hasNextPage && (
        <div className="text-center">
          <button
            onClick={() => { void products.fetchNextPage(); }}
            disabled={products.isFetchingNextPage}
            className="rounded-lg border border-line px-5 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:cursor-wait disabled:text-ink-3"
          >
            {products.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      {selected && <ProductDetailsDrawer product={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
